import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Loader } from "@tql/ui-components-react/kendo/indicators";
import { RadioButton } from "@tql/ui-components-react/kendo/inputs";
import { Card } from "@tql/ui-components-react/kendo/layout";
import { bank, trash_01 } from "@tql/ui-components-react/untitled/icons";

import { PaymentErrorModal } from "~/features/payment/error-modal/index.tsx";
import { useGetCustomerAccounts } from "~/features/payment/hooks/use-get-customer-accounts.ts";
import type { PaymentNonce, SavedAch } from "~/shared/types/payment-types.ts";

import { useDeletePaymentMethod } from "../../hooks/use-delete-payment-method.ts";

type SavedACHAccountProps = {
	handleApplyPaymentMethod: (paymentMethod: SavedAch | PaymentNonce) => void;
	savedAch: SavedAch | PaymentNonce;
	hideSelectionActions?: boolean;
	isSelected?: boolean;
};

export const SavedACHAccount = ({
	handleApplyPaymentMethod,
	savedAch,
	isSelected = false,
}: SavedACHAccountProps) => {
	const { accounts } = useGetCustomerAccounts();
	const { deletePaymentMethodAsync, isSubmitting, error, reset } =
		useDeletePaymentMethod();
	const bankName =
		"bankName" in savedAch
			? (savedAch.bankName ?? "Bank Account")
			: "Bank Account";

	const getSafeLastFour = () => {
		if ("Last4" in savedAch && savedAch.Last4) return savedAch.Last4;
		if ("lastTwo" in savedAch && savedAch.lastTwo) return savedAch.lastTwo;
		if ("accountNumberLast4" in savedAch && savedAch.accountNumberLast4)
			return savedAch.accountNumberLast4;
		return "####";
	};

	const safeLastFour = getSafeLastFour();

	const address = "address" in savedAch ? savedAch.address : "N/A";

	let token = "new-ach";
	if ("paymentMethodToken" in savedAch && savedAch.paymentMethodToken) {
		token = savedAch.paymentMethodToken;
	} else if ("nonce" in savedAch) {
		token = savedAch.nonce;
	}

	return (
		<>
			<PaymentErrorModal
				errorMessage={
					"An error occurred while deleting the payment method. Please try again."
				}
				isOpen={!!error}
				onClose={reset}
			/>
			<Card
				onClick={() => handleApplyPaymentMethod(savedAch)}
				style={{
					padding: "10px",
					marginBottom: "10px",
					cursor: "pointer",
				}}
			>
				{isSubmitting ? (
					<Loader size={"large"} type={"infinite-spinner"} />
				) : (
					<div
						data-testid={"payment-method-row"}
						style={{ display: "flex", alignItems: "center" }}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<RadioButton
								checked={isSelected}
								onChange={() => handleApplyPaymentMethod(savedAch)}
							>
								<SvgIcon
									icon={bank}
									size={"large"}
									style={{ marginLeft: "8px" }}
								/>
							</RadioButton>
						</div>

						<div
							key={`payment-ach-${token}`}
							style={{ flex: 1, textAlign: "center" }}
						>
							<h6>
								{bankName ?? "Bank Account"} **** {safeLastFour}
							</h6>
							<p
								style={{
									margin: "4px 0 0 0",
									fontSize: "14px",
									color: "var(--kendo-color-subtle)",
								}}
							>
								{address ?? "N/A"}
							</p>
							{"isDefault" in savedAch && savedAch.isDefault ? (
								<div style={{ marginLeft: "auto" }}>
									<p>Default Bank Account</p>
								</div>
							) : null}
						</div>
						<SvgIcon
							icon={trash_01}
							onClick={(e) => {
								e.stopPropagation();
								deletePaymentMethodAsync({
									paymentMerchantCustomerId:
										accounts?.paymentMerchantCustomerId ?? "",
									paymentMethodToken: savedAch.paymentMethodToken ?? "",
								});
							}}
							size={"xlarge"}
							style={{ color: "var(--kendo-color-error-on-surface)" }}
						/>
					</div>
				)}
			</Card>
		</>
	);
};
